import { useCallback, useState } from 'react';

import useIsMounted from '@swf/core/hooks/useIsMounted';
import useRequest from '@swf/core/hooks/useRequest';
import type {
  GetPlayersBasedOnStatusRequest,
  GetPlayersBasedOnStatusResponse,
  HgGetPlayersBasedOnStatus,
  HgGetPlayersBasedOnStatusReply,
  PlayerListElement
} from '@swf/generated/types/api/v0/SwHomeGames';
import { handleServerResponse } from '@swf/poker';

interface State {
  error?: string | null;
  message?: string | null;
  data: PlayerListElement[];
}

const initialResponse: State = { error: null, message: null, data: [] };

const useGetPlayersBasedOnStatus = ({ clubId }: { clubId: number }) => {
  const isMounted = useIsMounted();
  const request = useRequest();
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState(initialResponse);
  const handleGetPlayers = useCallback(
    ({ avatarIncluded = true, limit, statuses }: HgGetPlayersBasedOnStatus) => {
      setLoading(true);

      request({
        service: 'homeGames.getPlayersBasedOnStatusRequest',
        body: {
          getPlayersBasedOnStatus: { avatarIncluded, clubId, limit, statuses }
        } as GetPlayersBasedOnStatusRequest
      }).subscribe({
        next: ({ body }: { body: GetPlayersBasedOnStatusResponse }) => {
          const { error, data } = handleServerResponse<
            GetPlayersBasedOnStatusResponse,
            HgGetPlayersBasedOnStatusReply
          >({
            response: body,
            accessor: 'getPlayersBasedOnStatusReply'
          });
          if (!isMounted()) return;

          if (error) {
            setResponse((prevState) => ({
              ...prevState,
              error,
              data: []
            }));
          } else if (data) {
            setResponse((prevState) => ({
              ...prevState,
              error: null,
              data: data.playerList as PlayerListElement[]
            }));
          }

          setLoading(false);
        }
      });
    },

    [clubId, isMounted, request]
  );

  return { response, loading, handleGetPlayers };
};

export default useGetPlayersBasedOnStatus;
